import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { foodItemAPI, orderAPI, dishRequestAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  Package,
  ShoppingBag,
  TrendingUp,
  ChefHat,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  MessageSquare,
  CheckCircle,
  Clock,
  IndianRupee
} from 'lucide-react';
import SellerLayout from '../../components/layouts/SellerLayout';

export default function SellerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dishRequests, setDishRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptingRequest, setAcceptingRequest] = useState(null);
  
  // Determine base path based on user role (seller vs dealer)
  const basePath = user?.role === 'dealer' ? '/dealer' : '/seller';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [itemsRes, ordersRes, requestsRes] = await Promise.all([
        foodItemAPI.getAll({ seller_id: user.id }),
        orderAPI.getAll(user.id, 'seller'),
        dishRequestAPI.getAll(user.id, user.role, user.entity_id)
      ]);
      setItems(itemsRes.data);
      setOrders(ordersRes.data);
      setDishRequests(requestsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    setAcceptingRequest(requestId);
    try {
      await dishRequestAPI.accept(requestId, user.id);
      toast.success('Request accepted! Contact the buyer to fulfill.');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept request');
    } finally {
      setAcceptingRequest(null);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await foodItemAPI.delete(itemId);
      setItems(items.filter(i => i.id !== itemId));
      toast.success('Item deleted');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const pendingOrders = orders.filter(o => o.status === 'pending' || o.status === 'confirmed');
  const todayRevenue = orders
    .filter(o => {
      const orderDate = new Date(o.created_at).toDateString();
      const today = new Date().toDateString();
      return orderDate === today && o.status !== 'cancelled';
    })
    .reduce((sum, o) => sum + o.total_amount, 0);

  const stats = [
    { label: 'Active Items', value: items.filter(i => i.is_available).length, icon: ChefHat, color: 'text-primary' },
    { label: 'Pending Orders', value: pendingOrders.length, icon: Package, color: 'text-orange-500' },
    { label: 'Dish Requests', value: dishRequests.length, icon: MessageSquare, color: 'text-purple-500' },
    { label: "Today's Revenue", value: `₹${todayRevenue}`, icon: TrendingUp, color: 'text-secondary' },
  ];

  return (
    <SellerLayout>
      <div className="space-y-6" data-testid="seller-dashboard">
        {/* Welcome Section */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">Welcome, {user?.name}!</h1>
            <p className="text-muted-foreground">Manage your food offerings</p>
          </div>
          <Button 
            onClick={() => navigate(`${basePath}/items/new`)}
            className="rounded-full"
            data-testid="add-item-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat, idx) => (
            <Card key={idx} className="stats-card">
              <CardContent className="p-4">
                <stat.icon className={`w-8 h-8 ${stat.color} mb-2`} />
                <p className="text-2xl font-bold">{loading ? '...' : stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Dish Requests Section */}
        {dishRequests.length > 0 && (
          <Card className="border-purple-200 bg-purple-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-500" />
                Incoming Dish Requests
                <Badge className="ml-2 bg-purple-500">{dishRequests.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dishRequests.slice(0, 3).map(request => (
                  <div key={request.id} className="bg-white rounded-lg p-4 border border-purple-100">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-semibold">{request.title}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{request.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {request.meal_type.replace('_', ' ')}
                          </span>
                          {(request.budget_min || request.budget_max) && (
                            <span className="flex items-center gap-1">
                              <IndianRupee className="w-3 h-3" />
                              {request.budget_min && `₹${request.budget_min}`}
                              {request.budget_min && request.budget_max && ' - '}
                              {request.budget_max && `₹${request.budget_max}`}
                            </span>
                          )}
                          <span>From: {request.buyer_name}</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAcceptRequest(request.id)}
                        disabled={acceptingRequest === request.id}
                        className="bg-purple-500 hover:bg-purple-600"
                      >
                        {acceptingRequest === request.id ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Accept
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Recent Orders</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(`${basePath}/orders`)}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : pendingOrders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No pending orders</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingOrders.slice(0, 5).map((order) => (
                  <div 
                    key={order.id} 
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-xl"
                    data-testid={`order-${order.id}`}
                  >
                    <div>
                      <p className="font-semibold">{order.buyer_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {order.items.length} items • ₹{order.total_amount}
                      </p>
                    </div>
                    <Badge className={order.status === 'pending' ? 'status-pending' : 'status-confirmed'}>
                      {order.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Items */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">My Food Items</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(`${basePath}/items`)}>
              Manage All
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-8">
                <ChefHat className="w-12 h-12 mx-auto mb-2 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground mb-4">No items added yet</p>
                <Button onClick={() => navigate(`${basePath}/items/new`)} className="rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Item
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {items.slice(0, 4).map((item) => (
                  <div 
                    key={item.id} 
                    className="flex items-center gap-3 p-3 border border-border rounded-xl"
                    data-testid={`item-${item.id}`}
                  >
                    <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <ChefHat className="w-6 h-6 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{item.name}</p>
                      <p className="text-sm text-primary font-medium">
                        {item.is_free ? 'Free' : `₹${item.price}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`${basePath}/items/${item.id}/edit`)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteItem(item.id)}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SellerLayout>
  );
}
